import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, setLocale, ValidationError, AnyObject, SchemaFieldDescription } from 'yup';

/*
Usually error messages are just strings. But we need to translate them.
So we transform these error messages by error type into a format that we can translate with react-i18next
If you add a new error type, you need to add it in here
You cant add custom keys to this locale object.
In you custom validation logic you need to return those key and label objects.
*/
setLocale({
  mixed: {
    default: ({ label }) => ({ key: 'VALIDATION_ERROR_FIELD_INVALID', label }),
    required: ({ label }) => ({ key: 'VALIDATION_ERROR_FIELD_REQUIRED', label }),
    notType: ({ label, type }) => ({ key: `VALIDATION_ERROR_FIELD_NOT_TYPE_${type.toUpperCase()}`, label }),
  },
  array: {
    min: ({ label, min }) => ({ key: 'VALIDATION_ERROR_ARRAY_MIN', label, values: { min } }),
    max: ({ label, max }) => ({ key: 'VALIDATION_ERROR_ARRAY_MAX', label, values: { max } }),
  },
  number: {
    positive: 'VALIDATION_FIELD_MUST_BE_POSITIVE',
  },
});

export function useYupValidation<TValues extends AnyObject, TContext = AnyObject>(
  schema: ObjectSchema<TValues, TContext>
) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { t } = useTranslation();

  const validate = (values: unknown, context?: TContext): boolean => {
    try {
      schema.validateSync(values, { abortEarly: false, context });

      setErrors({});
      return true;
    } catch (error) {
      const newErrors: Record<string, string> = {};

      if (error instanceof ValidationError && error.inner) {
        error.inner.forEach((innerError) => {
          const error = innerError.errors[0];

          // errors from test() by default doesn't have a path, so we set it to 'form' because they're form-level
          const path = innerError.path || 'form';

          if (typeof error === 'string') {
            newErrors[path] = t(error, { label: t(innerError.params?.label) });
          } else {
            // typescript doesnt get we transformed error message with setLocale earlier into this format
            const { key, label, values } = error as unknown as {
              key: string;
              label: string;
              values?: Record<string, unknown>;
            };

            newErrors[path] = t(key, { label: t(label), ...values, interpolation: { escapeValue: false } });
          }
        });
      } else {
        // unexpected error
        throw error;
      }

      setErrors(newErrors);
      return false;
    }
  };

  const isFieldRequired = (name: string, options?: Parameters<typeof schema.describe>[0]): boolean => {
    const field: SchemaFieldDescription = schema.describe(options).fields[name];

    if (!field) {
      throw new Error(`Field "${name}" not found in schema`);
    }

    if ('optional' in field) {
      return !field.optional;
    }

    return true;
  };

  return { validate, errors, isFieldRequired };
}

export function isEmptyValidationResult(result: Record<string, unknown>): boolean {
  return result && Object.keys(result).length === 0;
}
